import { ColoursObj } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import i18n from "i18n/i18next";
import { useEffect, useState, type KeyboardEvent, type PointerEvent, type RefObject } from "react";
import { clampLeadingWidth } from "./pane-split";

const HANDLE_WIDTH = 12;
const KEY_STEP = 16;

interface PaneSplitterProps {
  /** The grid holding the two panes, the leading one first. */
  gridRef: RefObject<HTMLDivElement | null>;
  /** Moves the split while it is being dragged. */
  onMove: (leadingWidth: number) => void;
  /** Keeps the split where it was let go, as the leading pane's share of the panes' width. */
  onCommit: (split: number) => void;
  /** Goes back to the default split. */
  onReset: () => void;
}

/** The bar between the two panes, dragged or moved with the arrow keys to share the width out. */
export function PaneSplitter({ gridRef, onMove, onCommit, onReset }: PaneSplitterProps) {
  const [edge, setEdge] = useState<{ left: number; width: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Where the leading pane ends, whichever rule sized it.
  useEffect(() => {
    const grid = gridRef.current;
    const leading = grid?.firstElementChild as HTMLElement | null;
    if (!grid || !leading) return;
    const measure = () => setEdge({ left: leading.offsetLeft + leading.offsetWidth, width: grid.clientWidth });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    observer.observe(leading);
    return () => observer.disconnect();
  }, [gridRef]);

  if (!edge) return null;

  const leadingWidthAt = (clientX: number) => {
    const grid = gridRef.current!.getBoundingClientRect();
    return clampLeadingWidth(clientX - grid.left, grid.width);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragging) onMove(leadingWidthAt(event.clientX));
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    onCommit(leadingWidthAt(event.clientX) / gridRef.current!.clientWidth);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === "ArrowLeft" ? -KEY_STEP : event.key === "ArrowRight" ? KEY_STEP : 0;
    if (!step) return;
    event.preventDefault();
    onCommit(clampLeadingWidth(edge.left + step, edge.width) / edge.width);
  };

  const line = dragging ? ColoursObj.core.secondary : `${ColoursObj.navbar.icons}55`;

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={i18n.t("two-pane.resize")}
      aria-valuenow={Math.round((edge.left / edge.width) * 100)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => setDragging(false)}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: edge.left - HANDLE_WIDTH / 2,
        width: HANDLE_WIDTH,
        zIndex: 1100,
        cursor: "col-resize",
        touchAction: "none",
        display: "grid",
        placeItems: "center",
        outline: "none",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          bottom: 0,
          left: HANDLE_WIDTH / 2,
          borderLeft: `1px solid ${line}`,
        },
        "&:hover::before, &:focus-visible::before": { borderLeftColor: ColoursObj.core.secondary },
      }}
    >
      {/* The grip. */}
      <Box
        sx={{
          position: "relative",
          width: "4px",
          height: "40px",
          borderRadius: "2px",
          bgcolor: dragging ? ColoursObj.core.secondary : line,
        }}
      />
    </Box>
  );
}
